/**
 * Page-aware live help — CLIENT config.
 *
 * Maps each app page to the user-facing help shown by the chat widget:
 *   - proactiveTip       : a short, one-time dismissible bubble for that page
 *   - suggestedQuestions : tappable chips shown when the chat opens (the chip
 *                          text is the question sent to the assistant)
 *
 * The invisible `contextForAssistant` text and the curated answers to these
 * questions live SERVER-side (server/config/pageHelpContext.js). The client
 * only ever sends a short `pageId` key, which the server looks up in its own
 * whitelist — so the client can never inject arbitrary system-prompt text.
 *
 * The full human-readable table (tips + context + answers) is documented in
 * docs/CHAT_ASSISTANT.md. Keep all three in sync in the same commit.
 *
 * Voice: every string here follows the Mixtape Memories grief-sensitive brand
 * voice (see docs/CHAT_ASSISTANT.md Part 0 / the Brand Book, Section 4).
 */

// pageId → { proactiveTip, suggestedQuestions:[{ label, text }] }
export const PAGE_HELP = {
  dashboard: {
    proactiveTip:
      'Each project here saves where you left off. Open one to keep working, or start a new video when you’re ready.',
    suggestedQuestions: [
      { label: 'How do I start a new video?', text: 'How do I start a new video?' },
      { label: 'What do the status labels mean?', text: 'What do the status labels mean?' },
      { label: 'How long can I download my video?', text: 'How long can I download my video?' },
    ],
  },

  'project-editor:details': {
    proactiveTip:
      'You can change these details any time before you pay, and "Save for later" keeps what you\'ve entered so you can pick it up from your dashboard.',
    suggestedQuestions: [
      { label: 'Do I have to add a title?', text: 'Do I have to add a title?' },
      { label: 'Can I change these details later?', text: 'Can I change these details later?' },
      { label: 'Is my progress saved if I leave?', text: 'Is my progress saved if I leave?' },
    ],
  },

  'project-editor:template': {
    proactiveTip:
      'You can change the style later. It only changes the look, not your photos, captions or music, and you can preview it first.',
    suggestedQuestions: [
      { label: 'Can I change the style later?', text: 'Can I change the style later?' },
      { label: 'Slideshow vs animated styles?', text: 'What’s the difference between slideshow and animated styles?' },
      { label: 'Does the template change my photos?', text: 'Does the template change my photos?' },
    ],
  },

  'project-editor:photos': {
    proactiveTip:
      'Photographs need to be JPEG or PNG and under 20 MB each. A Make Your Own video holds up to 100, and you’ll arrange their order in the next step.',
    suggestedQuestions: [
      { label: 'What photo types and sizes can I use?', text: 'What photo types and sizes can I use?' },
      { label: 'How do I put my photos in order?', text: 'How do I put my photos in order?' },
      { label: 'My upload looks stuck. Is that ok?', text: 'My upload looks stuck near the end. Is something wrong?' },
    ],
  },

  'project-editor:keymoments': {
    proactiveTip:
      'This is where you set the order of your photos and add captions. Captions are optional and best kept short.',
    suggestedQuestions: [
      { label: 'How do I reorder my photos?', text: 'How do I reorder my photos?' },
      { label: 'Do I have to add captions?', text: 'Do I have to add captions?' },
      { label: 'What are opening and closing frames?', text: 'What are opening and closing frames?' },
    ],
  },

  'project-editor:keymoments-frames': {
    proactiveTip:
      'Opening and closing frames are optional title cards. The name and dates from your details are filled in for you, and you can edit or skip them.',
    suggestedQuestions: [
      { label: 'What are opening and closing frames?', text: 'What are opening and closing frames?' },
      { label: 'Do I have to add them?', text: 'Do I have to add opening and closing frames?' },
    ],
  },

  'project-editor:music': {
    proactiveTip:
      'You can play any track to hear it before choosing, and if a track is longer than your video, you can drag to set where the music starts.',
    suggestedQuestions: [
      { label: 'Can I hear a track first?', text: 'Can I hear a track before choosing?' },
      { label: 'Can I use my own music?', text: 'Can I use my own music?' },
      { label: 'Can I choose where the song starts?', text: 'Can I choose where the song starts?' },
      { label: 'What if I don’t want music?', text: 'What if I don’t want music?' },
    ],
  },

  'project-editor:preview': {
    proactiveTip:
      'Preview the video and make any changes before you continue. Your progress is saved, so you can come back and keep editing until you pay.',
    suggestedQuestions: [
      { label: 'Is the preview the final video?', text: 'Is the preview the final video?' },
      { label: 'Can I still make changes?', text: 'Can I still make changes?' },
      { label: 'How does the share link work?', text: 'How does the share link work?' },
    ],
  },

  payment: {
    proactiveTip:
      'Payment starts your final video. It renders afterwards and we’ll email you when it’s ready, usually within the hour.',
    suggestedQuestions: [
      { label: 'What happens after I pay?', text: 'What happens after I pay?' },
      { label: 'How do I use a coupon or gift code?', text: 'How do I use a coupon or gift code?' },
      { label: 'Can I get a refund?', text: 'Can I get a refund?' },
      { label: 'Problem with payment?', text: 'What if I have a problem with payment?' },
    ],
  },

  download: {
    proactiveTip:
      'Your video is being prepared and your work is safe. This page updates on its own, and if anything isn’t right, the support team is always ready to help.',
    suggestedQuestions: [
      { label: 'How long until my video is ready?', text: 'How long until my video is ready?' },
      { label: 'How long do I have to download it?', text: 'How long do I have to download it?' },
      { label: 'My render failed. What do I do?', text: 'My render failed. What do I do?' },
    ],
  },

  guide: {
    proactiveTip:
      'The easy steps are at the top and answers to common questions are below. Ask me anything, or call us on 0480 837 370.',
    suggestedQuestions: [
      { label: 'How much does a video cost?', text: 'How much does a video cost?' },
      { label: 'How many photos can I add?', text: 'How many photos can I add?' },
      { label: 'How long does the video take?', text: 'How long does the video take?' },
    ],
  },

  contact: {
    proactiveTip:
      'You can report a problem, request a feature, or ask for a call here. For a bug, a screenshot or two really helps us fix it.',
    suggestedQuestions: [
      { label: 'How do I report a bug?', text: 'How do I report a bug?' },
      { label: 'How do I request a feature?', text: 'How do I request a feature?' },
      { label: 'Can I speak to someone?', text: 'Can I speak to someone?' },
    ],
  },

  profile: {
    proactiveTip:
      'You can update your display name or change your password here. Changes are saved when you confirm them.',
    suggestedQuestions: [
      { label: 'How do I change my password?', text: 'How do I change my password?' },
      { label: 'How do I delete my account?', text: 'How do I delete my account?' },
    ],
  },

  'provider-signup': {
    proactiveTip:
      'This application has a few steps and you can move back and forth. Your answers stay as you go.',
    suggestedQuestions: [
      { label: 'What is partnership mode?', text: 'What is partnership mode?' },
    ],
  },

  'partner-guide': {
    proactiveTip:
      'The four ways to partner, your partner pricing and your referral code are all on this page.',
    suggestedQuestions: [
      { label: 'Where is my referral code?', text: 'Where is my referral code?' },
      { label: 'What do I pay per video?', text: 'What do I pay per video?' },
    ],
  },

  landing: {
    proactiveTip: null,
    suggestedQuestions: [],
  },
};

/**
 * Resolve the current pathname (+ optional editor sub-step) to a pageId.
 * Returns null when the page has no special help — the widget then behaves
 * exactly as a generic chat (the required fallback).
 *
 * @param {string} pathname   - React Router location.pathname
 * @param {string|null} editorStep - the wizard sub-step published by the
 *   editor via PageHelpProvider ('details' | 'template' | 'photos' |
 *   'keymoments' | 'keymoments-frames' | 'music' | 'preview'), or null.
 * @returns {string|null} pageId present in PAGE_HELP, or null.
 */
export function resolvePageId(pathname, editorStep = null) {
  if (!pathname || typeof pathname !== 'string') return null;

  // Normalise trailing slash (but keep root '/').
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;

  // Exact, static routes (most specific first).
  const STATIC = {
    '/': 'landing',
    '/dashboard': 'dashboard',
    '/dashboard/partner': 'partner-guide',
    '/profile': 'profile',
    '/provider-signup': 'provider-signup',
    '/guide': 'guide',
    '/contact': 'contact',
  };
  if (STATIC[path]) return STATIC[path];

  // Project payment / download (dynamic :id).
  if (/^\/projects\/[^/]+\/payment$/.test(path)) return 'payment';
  if (/^\/projects\/[^/]+\/download$/.test(path)) return 'download';

  // The creation wizard — one URL, many internal steps. The editor publishes
  // its current sub-step through PageHelpProvider; without it we return null
  // (generic chat) rather than guess a step.
  if (path === '/projects/new' || /^\/projects\/[^/]+\/edit$/.test(path)) {
    if (!editorStep) return null;
    const id = `project-editor:${editorStep}`;
    return PAGE_HELP[id] ? id : null;
  }

  return null;
}
